/**
 * ObservedRadarWidget.ts
 *
 * Real-time NOAA MRMS radar reflectivity and gauge-corrected precipitation card.
 */

import { ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from '../types';

export class ObservedRadarWidget implements WidgetRenderer {
  public renderHtml(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): string {
    const title = widget.title || 'Observed MRMS Radar & Precip';
    const obs = ctx.data.recentObserved;

    const rain24h = obs ? Number(obs.rain24hInches.toFixed(2)) : 0.00;
    const rain1h = obs ? Number(obs.rain1hInches.toFixed(2)) : 0.00;

    let dbz = 0;
    if (rain1h > 0) {
      // Marshall-Palmer Z-R conversion: Z = 200 * R^1.6 (R in mm/hr)
      const rMm = rain1h * 25.4;
      const z = 200 * Math.pow(Math.max(0.01, rMm), 1.6);
      dbz = Math.round(10 * Math.log10(Math.max(1, z)));
    }

    const isActive = dbz > 15 || rain1h > 0;
    const echoBadge = isActive ? 'Precipitation Active' : 'No Echoes Detected';
    const echoColor = isActive ? 'var(--hero-emerald, #10b981)' : '#94a3b8';

    return `
      <div class="metric-card" style="background: rgba(30, 41, 59, 0.75); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; padding: 14px 16px; box-shadow: 0 8px 24px rgba(0,0,0,0.3);">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
          <span style="font-size: 0.95rem; font-weight: 700; color: #fff;">📡 ${title}</span>
          <span style="background: rgba(14, 165, 233, 0.15); color: #38bdf8; border: 1px solid rgba(14, 165, 233, 0.3); font-size: 0.65rem; font-weight: 700; padding: 2px 7px; border-radius: 4px;">Live Radar</span>
        </div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
          <div style="background: rgba(15, 23, 42, 0.6); padding: 8px 10px; border-radius: 8px; border: 1px solid rgba(255, 255, 255, 0.06);">
            <div style="font-size: 0.65rem; color: #94a3b8; font-weight: 600; text-transform: uppercase;">Radar Reflectivity</div>
            <div style="font-size: 1.25rem; font-weight: 800; color: ${echoColor}; font-family: monospace; margin: 2px 0;">${dbz} <span style="font-size: 0.72rem; color: #94a3b8; font-weight: 500;">dBZ</span></div>
            <div style="font-size: 0.68rem; font-weight: 600; color: ${echoColor};">${echoBadge}</div>
          </div>
          <div style="background: rgba(15, 23, 42, 0.6); padding: 8px 10px; border-radius: 8px; border: 1px solid rgba(255, 255, 255, 0.06);">
            <div style="font-size: 0.65rem; color: #94a3b8; font-weight: 600; text-transform: uppercase;">Observed 24h Total</div>
            <div style="font-size: 1.25rem; font-weight: 800; color: #38bdf8; font-family: monospace; margin: 2px 0;">${rain24h.toFixed(2)}"</div>
            <div style="font-size: 0.68rem; color: #94a3b8;">Gauge Corrected (MRMS)</div>
          </div>
        </div>
      </div>
    `;
  }

  public init(_widget: ForecastWidgetConfig, _ctx: WidgetRenderContext): void {}
}
